<template>
  <div>
    <div class="card mb-3 d-print-none">
      <div class="card-body chart">
        <div class="row">
          <div class="col">
            <label for="lastNameFilter" class="form-label">Teacher Last Name:</label>
            <input id="lastNameFilter" v-model="lastNameFilter" class="form-control" type="text" placeholder="Enter last name">
          </div>
          <div class="col">
            <label for="firstNameFilter" class="form-label">Teacher First Name:</label>
            <input id="firstNameFilter" v-model="firstNameFilter" class="form-control" type="text" placeholder="Enter first name">
          </div>
        </div>
        <div class="row mt-2">
          <div class="col">
            <label for="roleFilter" class="form-label">Teacher Role:</label>
            <select id="roleFilter" v-model="selectedRole" class="form-control">
              <option value="All">
                All
              </option>
              <option value="Primary">
                Primary
              </option>
              <option value="Secondary">
                Secondary
              </option>
              <option value="Contributor">
                Contributor
              </option>
            </select>
          </div>
          <div class="col">
            <label for="topQuartileFilter" class="form-label mr-1">Top Quartile for Growth</label>
            <custom-tooltip
              content="A.C.A § 6-17-431 and A.C.A § 6-17-429 reference assigning students who have not met readiness standards to a teacher with a three-year growth score in the top quartile for ELA or Math. Refer to the Rules Governing Math Intervention and the Right to Read Rules for more information."
              :element-id="topQuartileFilter" />
            <select id="topQuartileFilter" v-model="topQuartileFilter" class="form-control">
              <option value="All">
                All
              </option>
              <option value="true">
                Yes
              </option>
              <option value="false">
                No
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>
    <paginated-table :items="teachers" :fields="fields" sort-by="lastName" :sort-desc="false">
      <template v-slot:cell(districtLEA)="data">
        <div>
          <button type="button" class="btn btn-link" @click="goToLea(data.value)" :id="data.index + '-' + data.value">
            {{ data.value }}
          </button>
          <lea-tooltip :target-selector="data.index + '-' + data.value" :lea-number="data.value" />
        </div>
      </template>
      <template v-slot:cell(caseId)="data">
        <aels-case-id-link :case-id="data.value" />
      </template>
      <template v-slot:cell(schoolLEA)="data">
        <div>
          <button type="button" class="btn btn-link" @click="goToLea(data.value)" :id="data.index + '-' + data.value">
            {{ data.value }}
          </button>
          <lea-tooltip :target-selector="data.index + '-' + data.value" :lea-number="data.value" />
        </div>
      </template>
      <template v-slot:cell(teacherLastName)="data" class=".chart">
        {{ data.value }}
      </template>
      <template v-slot:cell(teacherFirstName)="data">
        {{ data.value }}
      </template>
      <template v-slot:cell(role)="data">
        {{ data.value }}
      </template>
      <template v-slot:cell(growthScoreFY_2)="data">
        {{ data.value ? data.value.toFixed(2) : '' }}
      </template>
      <template v-slot:cell(growthScoreFY_1)="data">
        {{ data.value ? data.value.toFixed(2) : '' }}
      </template>
      <template v-slot:cell(growthScoreFY)="data">
        {{ data.value ? data.value.toFixed(2) : '' }}
      </template>
      <template v-slot:cell(threeYearAverageGrowthScore)="data">
        <strong>{{ data.value ? data.value.toFixed(2) : '' }}</strong>
      </template>
      <template v-slot:cell(topQuartileForGrowth)="data">
        <div>
          <i v-if="data.value" class="fa-lg fas fa-check text-success" />
          <span v-else-if="subject === 'Composite'">N/A</span>
        </div>
      </template>
      <template v-slot:head(threeYearAverageGrowthScore)="data">
        <mark>
          <custom-tooltip
            content="The three year average is weighted by the number of students in each year. Therefore, you cannot recreate this average by simply averaging across years."
            :element-id="`threeYearAverageGrowthScore-${data.label}`"
            :label="data.label" />
        </mark>
      </template>
    </paginated-table>
    <div v-if="topQuartileFilter === 'true' && subject === 'Composite'" role="alert" class="alert alert-warning mt-3">
      <p class="mb-0">
        A.C.A § 6-17-431 and A.C.A § 6-17-429 reference assigning students who have not met readiness standards to a teacher with a three-year growth score in the top quartile for ELA or Math. 
        Refer to the <a href="https://dese.ade.arkansas.gov/Files/Math_Intervention_Legal.pdf">Rules Governing Math Intervention</a> and the  <a href="https://dese.ade.arkansas.gov/Files/Right_to_Read_Act__Legal.pdf">Right to Read Rules</a> for more information.
      </p>
    </div>
  </div>
</template>

<script>
import PaginatedTable from '@/components/PaginatedTable.vue'
import LeaTooltip from '@/components/LeaTooltip.vue'
import AelsCaseIdLink from '@/components/AelsCaseIdLink.vue';
import CustomTooltip from '@/components/CustomTooltip.vue';
import { LeaTypes } from '@/helpers/leaTypes';
import { first } from 'lodash';
import { mapState } from 'vuex';


export default {
  components: {
    PaginatedTable,
    LeaTooltip,
    AelsCaseIdLink,
    CustomTooltip
  },
  computed: {
    ...mapState('tgsModule', ['fiscalYear', 'subject', 'teacherTrends']),
    fields() {
      const leaFields = [];
      if (this.selectedLea.type > LeaTypes.District) {
        leaFields.push({ key: 'districtLEA', label: 'District', sortable: true });
      }
      if (this.selectedLea.type > LeaTypes.School) {
        leaFields.push({ key: 'schoolLEA', label: 'School', sortable: true });
      }

      return [
          ...leaFields,
          { key: 'teacherLastName', label: 'Teacher Last Name', sortable: true },
          { key: 'teacherFirstName', label: 'Teacher First Name', sortable: true },
          { key: 'caseId', label: 'Case ID', sortable: true },
          { key: 'role', label: 'Role', sortable: true }
        ].concat([
          { key: 'growthScoreFY_2', label: `${(this.fiscalYear+1990)-2} ${this.subject} Growth Score`, sortable: true },
          { key: 'growthScoreFY_1', label: `${(this.fiscalYear+1990)-1} ${this.subject} Growth Score`, sortable: true },
          { key: 'growthScoreFY', label: `${(this.fiscalYear+1990)} ${this.subject} Growth Score`, sortable: true },
        ]).concat([
          { key: 'threeYearAverageGrowthScore', label: 'Student Weighted 3-Year Average', sortable: true },
          { key: 'topQuartileForGrowth', label: 'Top Quartile for Growth', sortable: true },
        ])
    },
    teachers() {
      let t = this.teacherTrends
      if (this.selectedRole != 'All') {
        t = t.filter(t => {
          return t.role === this.selectedRole
        })
      }
      if (this.selectedType !== 'All') {
        t = t.filter(t => {
          return t.subject === this.selectedType
        })
      }
      if (first(this.lastNameFilter)) {
        t = t.filter(t => {
          return t.teacherLastName.toLowerCase().includes(this.lastNameFilter.toLowerCase())
        })
      }
      if (first(this.firstNameFilter)) {
        t = t.filter(t => {
          return t.teacherFirstName.toLowerCase().includes(this.firstNameFilter.toLowerCase())
        })
      }
      if (this.topQuartileFilter !== 'All') {
        t = t.filter(t => {
          return t.topQuartileForGrowth === (this.topQuartileFilter === 'true')
        })
      }
      return t
    },
    // Add computed property for query params to pass to pdf-button
    queryParams() {
      return `&role=${this.selectedRole}&subject=${this.$store.state.tgsModule.subject || 'Composite'}`;
    }
  },
  data() {
    return {
      selectedRole: this.$route.query.role || 'All',
      selectedType: 'All',
      lastNameFilter: '',
      firstNameFilter: '',
      topQuartileFilter: 'All'
    }
  },
  watch: {
    subject: {
      handler() {
        this.getTeacherTrend()
      }
    },
    selectedLea: {
      immediate: true,
      handler() {
        this.getTeacherTrend()
      }
    },
    fiscalYear: {
      immediate: true,
      handler() {
        this.getTeacherTrend()
      }
    },
    // Watch for changes to selectedRole and update route
    selectedRole: {
      handler(newRole) {
        if (newRole !== this.$route.query.role) {
          this.$router.push({
            query: { 
              ...this.$route.query, 
              role: newRole 
            }
          }).catch(err => {
            console.error(err)
          });
        }
      }
    },
    // Watch for route changes
    '$route.query.role': {
      immediate: true,
      handler(role) {
        if (role && role !== this.selectedRole) {
          this.selectedRole = role;
        }
      }
    },
    '$route.query.subject': {
      immediate: true,
      handler(subject) {
        if (subject && subject !== this.$store.state.tgsModule.subject) {
          this.$store.commit('tgsModule/setSubject', subject);
        }
      }
    }
  },
  methods: {
    goToLea(leaNumber) {
      const lea = this.$store.getters['globalModule/leaDictionary'][leaNumber]
      window.scrollTo(0, 0)
      this.$store.dispatch('globalModule/changeSelectedLea', lea)
    },
    getTeacherTrend() {
      if (this.selectedLea && this.subject) {
        this.$store.dispatch('tgsModule/getTeacherTrends', { fiscalYear: this.fiscalYear, lea: this.selectedLea.number, subject: this.subject })
      }
    },
    excelExportData() {
      return {
        name: 'Teacher Growth Data',
        items: this.teacherTrends.map(e => {
          return {
            'District LEA': e.districtLEA,
            'School LEA': e.schoolLEA,
            'Teacher Last Name': e.teacherLastName,
            'Teacher First Name': e.teacherFirstName,
            'Role': e.role,
            [`${(this.fiscalYear+1990)-2} ${this.subject} Growth Score`]: e.growthScoreFY_2,
            [`${(this.fiscalYear+1990)-1} ${this.subject} Growth Score`]: e.growthScoreFY_1,
            [`${(this.fiscalYear+1990)} ${this.subject} Growth Score`]: e.growthScoreFY,
            '3-Year Average Growth Score': e.threeYearAverageGrowthScore,
            'Top Quartile for Growth': e.topQuartileForGrowth
          }
        })
      }
    }
  },
}
</script>
